import GridViewIcon from "@mui/icons-material/GridView"
import SearchIcon from "@mui/icons-material/Search"
import TocIcon from "@mui/icons-material/Toc"
import {
	Button,
	Divider,
	Grid,
	IconButton,
	InputBase,
	Paper,
} from "@mui/material"
import { useTheme } from "@mui/material/styles"
import {
	ChangeEvent,
	FormEvent,
	FunctionComponent,
	useCallback,
	useEffect,
	useState,
} from "react"
import "react-date-range/dist/styles.css" // main css file
import "react-date-range/dist/theme/default.css" // theme css file
import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { useDispatch, useSelector } from "react-redux"

import { useNavigate } from "react-router"
import CampaignsList from "../../components/campaigns/list"
import CampaignsEnhancedTable from "../../components/campaigns/table"
import SpinnerCard from "../../components/file/spinner/card"
import FiltersCorporate from "../../components/filters/corporate"
import Sort from "../../components/sort"
import { AppDispatch } from "../../store"
import {
	fetchCampaigns,
	getCampaignsPaginated,
} from "../../store/actions/campaigns"
import { resetDefault } from "../../store/files-list/files-slice"
import { RootState } from "../../store/reducers"

export interface ICorporatesProperties {}

const CampaignsListView: FunctionComponent<ICorporatesProperties> = () => {
	const theme = useTheme()
	const { t } = useTranslation()
	const navigate = useNavigate()
	const dispatch = useDispatch<AppDispatch>()
	const authState = useSelector((state: RootState) => state.auth)
	const isAuthenticate = authState?.isAuthenticate
	const bankSelectedState: BankEssential | undefined = useSelector(
		(state: RootState) => state.banksUser.selected
	)
	const campaignsLoadingState: boolean = useSelector(
		(state: RootState) => state.campaignsPaginated.loading
	)
	const campaignsFetchLoadingState = useSelector(
		(state: RootState) => state.campaignsPaginated.loadingFetch
	)
	const campaignsnextUrlState = useSelector(
		(state: RootState) => state.campaignsPaginated.list.nextUrl
	)
	const permissions = useSelector(
		(state: RootState) => state.auth?.user?.permissions
	)

	const hasCrudCampaignEnabled: boolean = permissions
		? permissions.some(
				(perm) =>
					perm.permissionName === "CRUD_CAMPAIGN" && perm.enabled === true
			)
		: false

	const [filterBank, setFilterBank] = useState<undefined | string>(
		authState.user?.idBank ?? bankSelectedState?.id
	)
	const [sortCriteria, setSortCriteria] = useState([
		{ field: "id", order: "asc" },
	])
	const [whatsappFilter, setWhatsappFilter] = useState<boolean | undefined>(
		undefined
	)
	const [autoAcceptFilter, setAutoAcceptFilter] = useState<boolean | undefined>(
		undefined
	)
	const [checkIbanFilter, setCheckIbanFilter] = useState<boolean | undefined>(
		undefined
	)
	const [filterName, setFilterName] = useState<undefined | string>(undefined)
	const [viewType, setViewType] = useState<undefined | string>("grid")

	const { ref, inView } = useInView({
		threshold: 1,
		triggerOnce: false,
	})

	const setFilters = useCallback(() => {
		const filter: { [key: string]: any } = {}

		if (filterBank) filter.bankId = filterBank
		if (filterName) filter.bankName = filterName
		if (typeof autoAcceptFilter === "boolean")
			filter.autoAccept = autoAcceptFilter

		if (typeof whatsappFilter === "boolean") filter.whatsapp = whatsappFilter

		if (typeof checkIbanFilter === "boolean") filter.checkIban = checkIbanFilter

		return filter
	}, [
		filterBank,
		filterName,
		checkIbanFilter,
		autoAcceptFilter,
		whatsappFilter,
	])

	const handleSearch = () => {
		const filters: { [key: string]: any } = setFilters()
		const userSort = sortCriteria
			.map((criteria) => `${criteria.field}:${criteria.order}`)
			.join(",")
		dispatch(
			getCampaignsPaginated({
				page: 0,
				pageSize: 10,
				filter: filters,
				sort: userSort,
			})
		)
			.then(() => {})
			.catch(() => {})
	}

	useEffect(() => {
		if (isAuthenticate) {
			handleSearch()
		}
	}, [isAuthenticate])

	useEffect(() => {
		resetDefault()
	}, [])

	useEffect(() => {
		!hasCrudCampaignEnabled && navigate("/")
	}, [hasCrudCampaignEnabled])

	useEffect(() => {
		if (inView && campaignsnextUrlState && !campaignsFetchLoadingState) {
			dispatch(fetchCampaigns({ url: campaignsnextUrlState }))
				.then(() => {})
				.catch(() => {})
		}
	}, [
		authState?.isAuthenticate,
		inView,
		campaignsnextUrlState,
		campaignsFetchLoadingState,
	])

	return (
		<>
			<Paper
				component="form"
				onSubmit={(event: FormEvent) => {
					event.stopPropagation()
					event.preventDefault()
					handleSearch()
				}}
				sx={{
					p: "2px 4px",
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					width: "100%",
				}}
			>
				<InputBase
					value={filterName}
					onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
						setFilterName(event.target.value)
					}
					sx={{ ml: 1, flex: 1 }}
					placeholder={t("search.placeholder", { ns: "campaign" })}
					inputProps={{
						"aria-label": t("search.placeholder", { ns: "campaign" }),
					}}
				/>
				<IconButton
					type="button"
					sx={{ p: "10px" }}
					aria-label="search"
					onClick={() => handleSearch()}
				>
					<SearchIcon />
				</IconButton>
				<Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
				<Grid container gap="16px" sx={{ width: "fit-content" }}>
					<FiltersCorporate
						filterBank={filterBank}
						setFilterBank={setFilterBank}
						autoAcceptFilter={autoAcceptFilter}
						setAutoAcceptFilter={setAutoAcceptFilter}
						checkIbanFilter={checkIbanFilter}
						setCheckIbanFilter={setCheckIbanFilter}
						handleSearch={handleSearch}
						whatsappFilter={whatsappFilter}
						setWhatsappFilter={setWhatsappFilter}
					/>
					<Sort
						handleSearch={handleSearch}
						sortCriteria={sortCriteria}
						setSortCriteria={setSortCriteria}
						sortingOptions={[
							"id",
							"creationDate",
							"bankName",
							"enabled",
							"whatsapp",
						]}
						availableOptionsInitial={[
							"creationDate",
							"bankName",
							"enabled",
							"whatsapp",
						]}
					/>
				</Grid>
			</Paper>

			<Grid
				container
				alignItems={"center"}
				justifyContent={"flex-end"}
				width={"100%"}
				sx={{ marginTop: "16px" }}
			>
				<Button
					variant={viewType === "grid" ? "contained" : "outlined"}
					onClick={(e) => setViewType("grid")}
					sx={{
						backgroundColor:
							viewType === "grid"
								? theme.palette.primary.main
								: theme.palette.secondary.main,
					}}
					startIcon={
						<GridViewIcon
							style={{
								backgroundColor:
									viewType === "grid"
										? theme.palette.primary.main
										: theme.palette.secondary.main,
								color:
									viewType === "grid"
										? theme.palette.primary.contrastText
										: theme.palette.secondary.contrastText,
							}}
						/>
					}
				></Button>
				<Button
					variant={viewType === "table" ? "contained" : "outlined"}
					onClick={(e) => setViewType("table")}
					sx={{
						marginLeft: "16px",
						backgroundColor:
							viewType === "table"
								? theme.palette.primary.main
								: theme.palette.secondary.main,
					}}
					startIcon={
						<TocIcon
							style={{
								backgroundColor:
									viewType === "table"
										? theme.palette.primary.main
										: theme.palette.secondary.main,
								color:
									viewType === "table"
										? theme.palette.primary.contrastText
										: theme.palette.secondary.contrastText,
							}}
						/>
					}
				></Button>
			</Grid>
			{campaignsLoadingState ? (
				<SpinnerCard />
			) : (
				<>
					{viewType === "grid" ? (
						<CampaignsList bankId={filterBank} />
					) : (
						<CampaignsEnhancedTable
							sortCriteria={sortCriteria}
							setSortCriteria={setSortCriteria}
						/>
					)}
					{campaignsFetchLoadingState ? (
						<SpinnerCard />
					) : (
						<div ref={ref} style={{ height: "30px" }}></div>
					)}
				</>
			)}
		</>
	)
}

export default CampaignsListView
